import { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchData,
  extendSchoolsBulk,
  changeSchoolClosed,
  updateSchoolPayDate,
  updateSchoolNote,
} from "../../redux/slices/mainDataSlice";
import { toast } from "react-hot-toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faCalendarPlus,
  faCheckDouble,
  faPowerOff,
  faEraser,
  faPaperPlane,
  faCheck,
} from "@fortawesome/free-solid-svg-icons";
import "../../main.scss";

// تحويل الأرقام الهندية/الفارسية إلى غربية (البيانات قد تحتوي هندية)
const normalizeDigits = (value) => {
  if (value == null) return value;
  return String(value)
    .replace(/[٠-٩]/g, (d) => "٠١٢٣٤٥٦٧٨٩".indexOf(d))
    .replace(/[۰-۹]/g, (d) => "۰۱۲۳۴۵۶۷۸۹".indexOf(d));
};

const addDays = (dateStr, days) => {
  const d = new Date(normalizeDigits(dateStr));
  if (isNaN(d.getTime())) return "-";
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

export default function ExtendSubscription() {
  const dispatch = useDispatch();
  const schools = useSelector(
    (state) => state.main.data?.schoolData || [],
    (a, b) => JSON.stringify(a) === JSON.stringify(b)
  );
  const loading = useSelector((state) => state.main.loading);

  const [activeTab, setActiveTab] = useState("expired");
  const [searchQuery, setSearchQuery] = useState("");
  const [days, setDays] = useState("");
  const [selected, setSelected] = useState({});
  const [isApplying, setIsApplying] = useState(false);
  // حالة تبويب المغلقة
  const [closedDays, setClosedDays] = useState({});
  const [busyRgmw, setBusyRgmw] = useState(null);
  const [msgModal, setMsgModal] = useState(null);
  const [msgText, setMsgText] = useState("");
  const [sendingMsg, setSendingMsg] = useState(false);

  useEffect(() => {
    dispatch(fetchData());
  }, [dispatch]);

  // تاريخ اليوم (بالتوقيت المحلي) بصيغة YYYY-MM-DD
  const todayStr = useMemo(() => {
    const t = new Date();
    const m = String(t.getMonth() + 1).padStart(2, "0");
    const d = String(t.getDate()).padStart(2, "0");
    return `${t.getFullYear()}-${m}-${d}`;
  }, []);

  // فقط المدارس العاملة التي انتهى تاريخ تمديدها (أقل من اليوم)
  const eligibleSchools = useMemo(
    () =>
      schools.filter(
        (s) =>
          s.paydate &&
          s.onoff !== "off" &&
          normalizeDigits(s.paydate).slice(0, 10) < todayStr
      ),
    [schools, todayStr]
  );

  // المدارس المغلقة
  const closedSchools = useMemo(
    () => schools.filter((s) => s.onoff === "off"),
    [schools]
  );

  const filteredSchools = useMemo(() => {
    if (!searchQuery.trim()) return eligibleSchools;
    const q = searchQuery.trim().toLowerCase();
    return eligibleSchools.filter(
      (s) =>
        s.schoolname?.toLowerCase().includes(q) ||
        String(s.rgmw).includes(q)
    );
  }, [eligibleSchools, searchQuery]);

  const filteredClosed = useMemo(() => {
    if (!searchQuery.trim()) return closedSchools;
    const q = searchQuery.trim().toLowerCase();
    return closedSchools.filter(
      (s) =>
        s.schoolname?.toLowerCase().includes(q) ||
        String(s.rgmw).includes(q)
    );
  }, [closedSchools, searchQuery]);

  const daysNum = parseInt(days, 10);
  const daysValid =
    Number.isInteger(daysNum) && daysNum > 0 && daysNum <= 365;

  const selectedIds = useMemo(
    () => Object.keys(selected).filter((k) => selected[k]),
    [selected]
  );

  const allSelected =
    filteredSchools.length > 0 &&
    selectedIds.length === filteredSchools.length;

  const toggleOne = (rgmw) => {
    setSelected((prev) => ({ ...prev, [rgmw]: !prev[rgmw] }));
  };

  const toggleAll = () => {
    if (allSelected) {
      setSelected({});
    } else {
      const next = {};
      filteredSchools.forEach((s) => {
        next[s.rgmw] = true;
      });
      setSelected(next);
    }
  };

  const handleApply = async () => {
    if (!daysValid) return toast.error("أدخل عدد أيام صالح (1 - 365)");
    if (selectedIds.length === 0) return toast.error("اختر مدرسة واحدة على الأقل");
    if (
      !window.confirm(
        `تمديد ${selectedIds.length} مدرسة لمدة ${daysNum} يوم؟`
      )
    )
      return;

    setIsApplying(true);
    try {
      const res = await dispatch(
        extendSchoolsBulk({ schools: selectedIds, days: daysNum })
      ).unwrap();
      toast.success(res.message || "تم التمديد بنجاح");
      setSelected({});
      setDays("");
      dispatch(fetchData(true));
    } catch {
      toast.error("فشل التمديد");
    } finally {
      setIsApplying(false);
    }
  };

  // الأيام المتبقية حتى انتهاء الاشتراك (سالب = منتهٍ منذ)
  const daysLeft = (paydate) => {
    if (!paydate) return null;
    const p = new Date(normalizeDigits(paydate).slice(0, 10));
    const t = new Date(todayStr);
    if (isNaN(p.getTime())) return null;
    return Math.round((p - t) / 86400000);
  };

  const daysLeftText = (paydate) => {
    const d = daysLeft(paydate);
    if (d == null) return "—";
    if (d > 0) return `متبقٍ ${d} يوم`;
    if (d === 0) return "ينتهي اليوم";
    return `منتهٍ منذ ${Math.abs(d)} يوم`;
  };

  const validClosedDays = (rgmw) => {
    const n = parseInt(closedDays[rgmw], 10);
    return Number.isInteger(n) && n > 0 && n <= 365 ? n : null;
  };

  const handleToggleClosed = async (school) => {
    setBusyRgmw(school.rgmw);
    try {
      await dispatch(changeSchoolClosed(school.rgmw)).unwrap();
      dispatch(fetchData(true));
    } catch {
      toast.error("فشل تغيير الحالة");
    } finally {
      setBusyRgmw(null);
    }
  };

  const handleExtendOne = async (school) => {
    const d = validClosedDays(school.rgmw);
    if (!d) return toast.error("أدخل عدد أيام صالح (1 - 365)");
    setBusyRgmw(school.rgmw);
    try {
      const res = await dispatch(
        extendSchoolsBulk({ schools: [String(school.rgmw)], days: d })
      ).unwrap();
      toast.success(res.message || "تم التمديد بنجاح");
      setClosedDays((prev) => ({ ...prev, [school.rgmw]: "" }));
      dispatch(fetchData(true));
    } catch {
      toast.error("فشل التمديد");
    } finally {
      setBusyRgmw(null);
    }
  };

  const handleClearDate = async (school) => {
    if (!window.confirm(`مسح تاريخ التمديد لمدرسة ${school.schoolname}؟`))
      return;
    setBusyRgmw(school.rgmw);
    try {
      await dispatch(
        updateSchoolPayDate({ rgmw: school.rgmw, date: null })
      ).unwrap();
      toast.success("تم مسح التاريخ");
      dispatch(fetchData(true));
    } catch {
      toast.error("فشل مسح التاريخ");
    } finally {
      setBusyRgmw(null);
    }
  };

  const handleSendMessage = async () => {
    if (!msgText.trim()) return toast.error("اكتب الرسالة أولاً");
    setSendingMsg(true);
    try {
      await dispatch(
        updateSchoolNote({
          rgmw: msgModal.rgmw,
          noteforschool: msgText.trim(),
        })
      ).unwrap();
      toast.success("تم إرسال الرسالة");
      setMsgModal(null);
      setMsgText("");
    } catch {
      toast.error("فشل إرسال الرسالة");
    } finally {
      setSendingMsg(false);
    }
  };

  return (
    <div className="extend-wrapper anim-fade-in">
      <div className="premium-edit-container" style={{ maxWidth: "1280px" }}>
        <div className="premium-header-modern">
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <div className="header-icon-box renewal-icon">
              <FontAwesomeIcon icon={faCalendarPlus} />
            </div>
            <div className="header-text-box">
              <h2>تمديد الاشتراك</h2>
              <div className="breadcrumb-info">
                <span>المدارس</span>
                <span className="separator">/</span>
                <span className="current-page">تمديد الاشتراك</span>
                <span className="separator">•</span>
                <span>
                  {activeTab === "expired"
                    ? `${eligibleSchools.length} مدرسة منتهية`
                    : `${closedSchools.length} مدرسة مغلقة`}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="premium-table-container">
          <div className="extend-tabs">
            <button
              type="button"
              className={`extend-tab ${activeTab === "expired" ? "active" : ""}`}
              onClick={() => setActiveTab("expired")}
            >
              المنتهية ({eligibleSchools.length})
            </button>
            <button
              type="button"
              className={`extend-tab ${activeTab === "closed" ? "active" : ""}`}
              onClick={() => setActiveTab("closed")}
            >
              المغلقة ({closedSchools.length})
            </button>
          </div>

          {activeTab === "expired" ? (
            <>
              <div className="extend-toolbar">
                <div className="global-search-container">
                  <FontAwesomeIcon icon={faSearch} className="search-icon" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="ابحث باسم المدرسة أو رقمها..."
                  />
                </div>
                <div className="extend-days-field">
                  <input
                    type="text"
                    inputMode="numeric"
                    value={days}
                    onChange={(e) => setDays(normalizeDigits(e.target.value).replace(/[^0-9]/g, ""))}
                    placeholder="عدد الأيام"
                  />
                </div>
                <button
                  className="extend-apply-btn"
                  onClick={handleApply}
                  disabled={isApplying || !daysValid || selectedIds.length === 0}
                >
                  <FontAwesomeIcon icon={faCheckDouble} />
                  {isApplying
                    ? "جارٍ التمديد..."
                    : `تطبيق التمديد (${selectedIds.length})`}
                </button>
              </div>

              <label className="extend-select-all">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleAll}
                />
                <span>تحديد الكل ({filteredSchools.length})</span>
              </label>

              {loading ? (
                <div className="extend-empty">جارٍ التحميل...</div>
              ) : filteredSchools.length === 0 ? (
                <div className="extend-empty">لا توجد مدارس مطابقة</div>
              ) : (
                <div className="extend-list">
                  {filteredSchools.map((school, idx) => {
                    const isChecked = !!selected[school.rgmw];
                    return (
                      <div
                        key={school.rgmw}
                        className={`extend-item ${isChecked ? "selected" : ""}`}
                        onClick={() => toggleOne(school.rgmw)}
                      >
                        <span className="extend-item__num">{idx + 1}</span>
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => toggleOne(school.rgmw)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <div className="extend-item__info">
                          <span className="extend-item__name">
                            {school.schoolname}
                          </span>
                          <span className="extend-item__number">
                            رقم المدرسة: {school.rgmw}
                          </span>
                        </div>
                        <div className="extend-item__dates">
                          <span className="extend-item__date">
                            ينتهي: {normalizeDigits(school.paydate)}
                          </span>
                          {daysValid && (
                            <span className="extend-item__date extend-item__date--new">
                              بعد التمديد: {addDays(school.paydate, daysNum)}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          ) : (
            <>
              <div className="extend-toolbar">
                <div className="global-search-container">
                  <FontAwesomeIcon icon={faSearch} className="search-icon" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="ابحث باسم المدرسة أو رقمها..."
                  />
                </div>
              </div>

              {loading ? (
                <div className="extend-empty">جارٍ التحميل...</div>
              ) : filteredClosed.length === 0 ? (
                <div className="extend-empty">لا توجد مدارس مغلقة</div>
              ) : (
                <div className="extend-list">
                  {filteredClosed.map((school, idx) => {
                    const busy = busyRgmw === school.rgmw;
                    const left = daysLeft(school.paydate);
                    return (
                      <div key={school.rgmw} className="extend-closed-item">
                        <span className="extend-item__num">{idx + 1}</span>
                        <div className="extend-closed-item__main">
                          <div className="extend-item__info">
                            <span className="extend-item__name">
                              {school.schoolname}
                            </span>
                            <span className="extend-item__number">
                              رقم المدرسة: {school.rgmw}
                            </span>
                          </div>
                          <div className="extend-closed-item__meta">
                            <span className="extend-item__date">
                              تاريخ التمديد:{" "}
                              {school.paydate
                                ? normalizeDigits(school.paydate).slice(0, 10)
                                : "—"}
                            </span>
                            <span
                              className={`extend-item__date ${
                                left != null && left < 0
                                  ? "extend-item__date--danger"
                                  : left != null && left <= 7
                                  ? "extend-item__date--warn"
                                  : ""
                              }`}
                            >
                              الأيام المتبقية: {daysLeftText(school.paydate)}
                            </span>
                          </div>
                        </div>
                        <div className="extend-closed-item__controls">
                          <div className="extend-closed-extend">
                            <input
                              type="text"
                              inputMode="numeric"
                              value={closedDays[school.rgmw] || ""}
                              onChange={(e) =>
                                setClosedDays((prev) => ({
                                  ...prev,
                                  [school.rgmw]: normalizeDigits(
                                    e.target.value
                                  ).replace(/[^0-9]/g, ""),
                                }))
                              }
                              placeholder="أيام"
                              disabled={busy}
                            />
                            <button
                              type="button"
                              className="extend-mini-btn extend-mini-btn--apply"
                              onClick={() => handleExtendOne(school)}
                              disabled={busy}
                              title="تطبيق التمديد"
                            >
                              <FontAwesomeIcon icon={faCheck} />
                              <span className="extend-mini-btn__label">تطبيق</span>
                            </button>
                            <button
                              type="button"
                              className="extend-mini-btn"
                              onClick={() => handleClearDate(school)}
                              disabled={busy || !school.paydate}
                              title="مسح تاريخ التمديد"
                            >
                              <FontAwesomeIcon icon={faEraser} />
                              <span className="extend-mini-btn__label">مسح</span>
                            </button>
                          </div>
                          <button
                            type="button"
                            className="extend-mini-btn extend-mini-btn--toggle"
                            onClick={() => handleToggleClosed(school)}
                            disabled={busy}
                          >
                            <FontAwesomeIcon icon={faPowerOff} />
                            <span className="extend-mini-btn__label">
                              {busy ? "جارٍ..." : "تفعيل"}
                            </span>
                          </button>
                          <button
                            type="button"
                            className="extend-mini-btn"
                            onClick={() => {
                              setMsgModal({
                                rgmw: school.rgmw,
                                schoolname: school.schoolname,
                              });
                              setMsgText("");
                            }}
                            title="إرسال رسالة خاصة"
                          >
                            <FontAwesomeIcon icon={faPaperPlane} />
                            <span className="extend-mini-btn__label">رسالة</span>
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {msgModal && (
        <div
          className="extend-modal-overlay"
          onClick={() => setMsgModal(null)}
        >
          <div
            className="extend-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>رسالة خاصة إلى {msgModal.schoolname}</h3>
            <textarea
              value={msgText}
              onChange={(e) => setMsgText(e.target.value)}
              placeholder="اكتب الرسالة..."
              rows={4}
            />
            <div className="extend-modal__actions">
              <button
                type="button"
                className="extend-mini-btn"
                onClick={() => setMsgModal(null)}
              >
                إلغاء
              </button>
              <button
                type="button"
                className="extend-mini-btn extend-mini-btn--apply"
                onClick={handleSendMessage}
                disabled={sendingMsg}
              >
                <FontAwesomeIcon icon={faPaperPlane} />
                <span className="extend-mini-btn__label">
                  {sendingMsg ? "جارٍ الإرسال..." : "إرسال"}
                </span>
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .extend-wrapper {
          padding: 5px;
          color: var(--text);
          font-family: 'Cairo', sans-serif;
          font-weight: 400;
        }

        .premium-edit-container {
          background: transparent;
          border: none;
          border-radius: 0;
          margin: 0 auto;
          overflow: visible;
          box-shadow: none;
          width: 100%;
          position: relative;
        }
        .premium-header-modern {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 30px;
          background: rgba(255, 255, 255, 0.7);
          backdrop-filter: blur(10px);
          border: 1px solid var(--border);
          border-radius: 6px 6px 0 0;
          gap: 20px;
        }
        .premium-table-container {
          padding: 15px;
          background: rgba(255, 255, 255, 0.5);
          backdrop-filter: blur(10px);
          border: 1px solid var(--border);
          border-top: none;
          border-radius: 0 0 6px 6px;
        }
        .header-icon-box {
          width: 48px; height: 48px;
          background: var(--accent);
          border-radius: 6px;
          display: flex; align-items: center; justify-content: center;
          font-size: 20px; color: white;
          flex-shrink: 0;
        }
        .header-icon-box.renewal-icon { background: linear-gradient(135deg, #28917f, #1d7567); }
        .header-text-box h2 { margin: 0; font-size: 20px; font-weight: 400; }
        .breadcrumb-info { display: flex; align-items: center; gap: 4px; color: var(--muted); font-size: 12px; margin-top: 2px; }
        .breadcrumb-info span:first-child { color: #28917f; font-weight: 400; }
        .current-page { color: #1d7567; font-weight: 400; }
        .separator { color: var(--muted); }

        .extend-tabs {
          display: flex;
          gap: 8px;
          margin-bottom: 12px;
        }
        .extend-tab {
          padding: 8px 20px;
          border: 1px solid var(--border);
          background: var(--bg);
          color: var(--muted);
          border-radius: 6px;
          font-size: 14px;
          font-family: 'Cairo', sans-serif;
          cursor: pointer;
        }
        .extend-tab.active {
          background: #28917f;
          border-color: #28917f;
          color: #fff;
        }

        .global-search-container {
          background: var(--bg);
          border: 1px solid var(--border);
          border-radius: 6px;
          padding: 0 15px;
          display: flex;
          align-items: center;
          gap: 12px;
          height: 44px;
          box-sizing: border-box;
        }
        .global-search-container:focus-within { border-color: #28917f; box-shadow: 0 0 0 3px rgba(40, 145, 127, 0.1); }
        .global-search-container .search-icon { color: #28917f; font-size: 14px; }
        .global-search-container input {
          background: transparent; border: none; color: var(--text);
          font-size: 14px; width: 100%; outline: none;
          font-family: 'Cairo', sans-serif; font-weight: 400;
        }

        .extend-toolbar {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 12px;
        }
        .extend-toolbar .global-search-container {
          flex: 1;
          min-width: 0;
        }
        .extend-days-field {
          background: rgba(40, 145, 127, 0.08);
          border: 1px solid rgba(40, 145, 127, 0.4);
          border-radius: 6px;
          padding: 0 14px;
          display: flex;
          align-items: center;
          height: 44px;
          box-sizing: border-box;
          width: 130px;
          flex-shrink: 0;
        }
        .extend-days-field:focus-within { border-color: #28917f; box-shadow: 0 0 0 3px rgba(40, 145, 127, 0.15); }
        .extend-days-field input::placeholder { color: #28917f; opacity: 0.7; }
        .extend-days-field input {
          background: transparent;
          border: none;
          color: var(--text);
          font-size: 14px;
          font-family: 'Cairo', sans-serif;
          outline: none;
          width: 100%;
          text-align: center;
        }
        .extend-apply-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          height: 44px;
          padding: 0 20px;
          background: #28917f;
          color: #fff;
          border: none;
          border-radius: 6px;
          font-size: 14px;
          font-family: 'Cairo', sans-serif;
          cursor: pointer;
          white-space: nowrap;
        }
        .extend-apply-btn:hover:not(:disabled) { background: #1d7567; }
        .extend-apply-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .extend-select-all {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 10px;
          font-size: 13px;
          color: var(--muted);
          cursor: pointer;
          user-select: none;
        }
        .extend-select-all input {
          width: 16px;
          height: 16px;
          accent-color: #28917f;
          cursor: pointer;
        }

        .extend-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .extend-item {
          display: flex;
          align-items: center;
          gap: 12px;
          background: var(--bg);
          border: 1px solid var(--border);
          border-radius: 6px;
          padding: 12px 14px;
          cursor: pointer;
        }
        .extend-item.selected {
          border-color: #28917f;
          background: rgba(40, 145, 127, 0.05);
        }
        .extend-item__num {
          display: flex;
          align-items: center;
          justify-content: center;
          min-width: 28px;
          height: 28px;
          border-radius: 6px;
          background: rgba(40, 145, 127, 0.1);
          color: #1d7567;
          font-size: 13px;
          flex-shrink: 0;
        }
        .extend-item input[type="checkbox"] {
          width: 18px;
          height: 18px;
          accent-color: #28917f;
          cursor: pointer;
          flex-shrink: 0;
        }
        .extend-item__info {
          display: flex;
          flex-direction: column;
          gap: 2px;
          flex: 1;
          min-width: 0;
        }
        .extend-item__name {
          font-size: 14px;
          color: var(--text);
        }
        .extend-item__number {
          font-size: 12px;
          color: var(--muted);
        }
        .extend-item__dates {
          display: flex;
          flex-direction: column;
          gap: 2px;
          text-align: left;
          flex-shrink: 0;
        }
        .extend-item__date {
          font-size: 12px;
          color: var(--muted);
          white-space: nowrap;
        }
        .extend-item__date--new {
          color: #28917f;
        }
        .extend-item__date--danger {
          color: #dc2626;
        }
        .extend-item__date--warn {
          color: #C28E00;
        }
        .extend-empty {
          text-align: center;
          padding: 40px 0;
          color: var(--muted);
          font-size: 14px;
        }

        .extend-closed-item {
          display: flex;
          align-items: center;
          gap: 12px;
          background: var(--bg);
          border: 1px solid var(--border);
          border-radius: 6px;
          padding: 12px 14px;
        }
        .extend-closed-item__main {
          display: flex;
          align-items: center;
          gap: 12px;
          flex: 1;
          min-width: 0;
        }
        .extend-closed-item__meta {
          display: flex;
          flex-direction: column;
          gap: 2px;
          text-align: left;
          flex-shrink: 0;
        }
        .extend-closed-item__controls {
          display: flex;
          align-items: center;
          gap: 6px;
          flex-wrap: wrap;
          flex-shrink: 0;
        }
        .extend-closed-extend {
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .extend-closed-extend input {
          width: 64px;
          height: 34px;
          border: 1px solid rgba(40, 145, 127, 0.4);
          background: rgba(40, 145, 127, 0.08);
          border-radius: 6px;
          text-align: center;
          font-size: 13px;
          font-family: 'Cairo', sans-serif;
          color: var(--text);
          outline: none;
        }
        .extend-closed-extend input:focus {
          border-color: #28917f;
          box-shadow: 0 0 0 3px rgba(40, 145, 127, 0.15);
        }
        .extend-mini-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          height: 34px;
          padding: 0 12px;
          border: 1px solid var(--border);
          background: var(--bg);
          color: var(--text);
          border-radius: 6px;
          font-size: 13px;
          font-family: 'Cairo', sans-serif;
          cursor: pointer;
          white-space: nowrap;
        }
        .extend-mini-btn:hover:not(:disabled) { border-color: #28917f; }
        .extend-mini-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .extend-mini-btn--apply {
          background: #28917f;
          border-color: #28917f;
          color: #fff;
        }
        .extend-mini-btn--apply:hover:not(:disabled) {
          background: #1d7567;
          border-color: #1d7567;
        }
        .extend-mini-btn--toggle {
          border-color: rgba(22, 163, 74, 0.5);
          color: #16a34a;
        }
        .extend-mini-btn--toggle:hover:not(:disabled) {
          background: rgba(22, 163, 74, 0.1);
          border-color: #16a34a;
        }

        .extend-modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.4);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10000;
          padding: 16px;
        }
        .extend-modal {
          background: var(--bg);
          border: 1px solid var(--border);
          border-radius: 6px;
          padding: 20px;
          width: 100%;
          max-width: 440px;
          font-family: 'Cairo', sans-serif;
        }
        .extend-modal h3 {
          margin: 0 0 12px;
          font-size: 16px;
          font-weight: 400;
          color: var(--text);
        }
        .extend-modal textarea {
          width: 100%;
          box-sizing: border-box;
          border: 1px solid var(--border);
          border-radius: 6px;
          padding: 10px 12px;
          font-size: 14px;
          font-family: 'Cairo', sans-serif;
          color: var(--text);
          background: var(--bg);
          outline: none;
          resize: vertical;
        }
        .extend-modal textarea:focus { border-color: #28917f; }
        .extend-modal__actions {
          display: flex;
          justify-content: flex-start;
          gap: 8px;
          margin-top: 12px;
        }

        @media (max-width: 768px) {
          .extend-toolbar {
            flex-wrap: wrap;
          }
          .extend-toolbar .global-search-container {
            flex: 1 1 100%;
            max-width: none;
          }
          .extend-days-field {
            flex: 1;
            width: auto;
          }
          .extend-apply-btn {
            flex: 1;
            justify-content: center;
          }
          .extend-item {
            flex-wrap: wrap;
          }
          .extend-item__dates {
            text-align: right;
            flex-basis: 100%;
            flex-direction: row;
            gap: 12px;
          }
          .extend-closed-item {
            flex-wrap: wrap;
          }
          .extend-closed-item__main {
            flex: 1 1 100%;
          }
          .extend-closed-item__meta {
            text-align: right;
          }
          .extend-closed-item__controls {
            flex: 1 1 100%;
          }
          .extend-mini-btn__label {
            display: none;
          }
          .extend-mini-btn {
            padding: 0 10px;
          }
        }
      `}</style>
    </div>
  );
}
